import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  LockOutlined,
  ManageAccountsOutlined,
  ShieldOutlined,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { useAuth } from "../../context/AuthContext";
import { authService } from "../../services";

const getPasswordStrength = (password) => {
  const raw = String(password || "");
  if (!raw) return 0;

  let score = Math.min(raw.length * 8, 40);
  if (/[a-z]/.test(raw)) score += 15;
  if (/[A-Z]/.test(raw)) score += 15;
  if (/\d/.test(raw)) score += 15;
  if (/[^A-Za-z0-9]/.test(raw)) score += 15;
  return Math.max(0, Math.min(score, 100));
};

const getStrengthColor = (score) => {
  if (score < 40) return "#E24B4A";
  if (score < 70) return "#BA7517";
  return "#1D9E75";
};

const extractErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") {
    return "No se pudo actualizar la contrasena.";
  }
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];
  if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
  if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  return "No se pudo actualizar la contrasena.";
};

function InfoCard({ label, value }) {
  return (
    <Box
      sx={(theme) => ({
        p: 1.45,
        borderRadius: 2,
        bgcolor: theme.palette.action.hover,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
      })}
    >
      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{label}</Typography>
      <Typography sx={{ mt: 0.4, fontSize: 13, fontWeight: 500, lineHeight: 1.4 }}>
        {value || "-"}
      </Typography>
    </Box>
  );
}

function ProfilePage() {
  const theme = useTheme();
  const { user, currentBranch } = useAuth();
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    new_password2: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const currentUserBranch = useMemo(() => {
    const branches = Array.isArray(user?.user_branches) ? user.user_branches : [];
    return branches.find((item) => item?.current) || branches[0] || null;
  }, [user?.user_branches]);

  const fullName = useMemo(() => {
    const value = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
    return value || user?.username || "Usuario";
  }, [user?.first_name, user?.last_name, user?.username]);

  const initials = useMemo(() => {
    const firstInitial = String(user?.first_name || user?.username || "U").trim().charAt(0);
    const lastInitial = String(user?.last_name || "").trim().charAt(0);
    return `${firstInitial}${lastInitial}`.trim().toUpperCase() || "U";
  }, [user?.first_name, user?.last_name, user?.username]);

  const branchCount = Array.isArray(user?.user_branches) ? user.user_branches.length : 0;
  const passwordStrength = useMemo(() => getPasswordStrength(form.new_password), [form.new_password]);
  const passwordsMatch =
    Boolean(form.new_password) &&
    Boolean(form.new_password2) &&
    form.new_password === form.new_password2;
  const canSubmit =
    Boolean(form.current_password) &&
    Boolean(form.new_password) &&
    form.new_password.length >= 8 &&
    passwordsMatch &&
    !submitting;

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError("");
    try {
      await authService.changePassword(form);
      setForm({
        current_password: "",
        new_password: "",
        new_password2: "",
      });
      showSnack("Contrasena actualizada correctamente.", "success");
    } catch (nextError) {
      setError(extractErrorMessage(nextError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8 }}>
      <Box sx={{ "& .MuiTypography-h4, & .MuiTypography-h5": { fontWeight: 500 } }}>
        <MainText
          icon={ManageAccountsOutlined}
          title="Mi perfil"
          subtitle="Consulta tus datos y administra solo tu seguridad de acceso"
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "1.04fr 0.96fr" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 2.2,
            borderRadius: 2.5,
            background: `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.08)} 100%)`,
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.6}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Stack direction="row" spacing={1.4} alignItems="center">
                <Avatar
                  sx={{
                    width: 58,
                    height: 58,
                    bgcolor: alpha(theme.palette.primary.main, 0.14),
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                  }}
                >
                  {initials}
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: 22, fontWeight: 500, lineHeight: 1.15 }}>
                    {fullName}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.35, fontSize: 13 }}>
                    Usuario @{user?.username || "sin-usuario"}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip size="small" color="primary" label="Solo lectura" />
                <Chip
                  size="small"
                  variant="outlined"
                  label={user?.is_superuser ? "Soporte" : "Cuenta operativa"}
                />
              </Stack>
            </Stack>

            <Divider />

            <Typography color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.7 }}>
              La informacion personal de esta cuenta se consulta aqui en modo lectura. Si algun
              dato requiere correccion, debe gestionarse por el flujo administrativo.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                gap: 1.2,
              }}
            >
              <InfoCard label="Nombre completo" value={fullName} />
              <InfoCard label="Usuario" value={user?.username || "-"} />
              <InfoCard label="DPI" value={user?.dpi || "Sin registro"} />
              <InfoCard label="Sucursal actual" value={currentBranch?.name || "Sin sucursal"} />
              <InfoCard label="Rol actual" value={currentUserBranch?.role?.name || "Sin rol"} />
              <InfoCard label="Sucursales asignadas" value={String(branchCount)} />
            </Box>
          </Stack>
        </Paper>

        <Stack spacing={2}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.2,
              borderRadius: 2.5,
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={(currentTheme) => ({
                  width: 42,
                  height: 42,
                  borderRadius: 1.75,
                  bgcolor: currentTheme.palette.action.hover,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                })}
              >
                <ShieldOutlined color="primary" fontSize="small" />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 18, fontWeight: 500 }}>
                  Seguridad de acceso
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.3, fontSize: 13 }}>
                  Desde esta pantalla solo se gestiona el cambio de contrasena.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 2.2,
              borderRadius: 2.5,
              borderTop: `3px solid ${theme.palette.primary.main}`,
            }}
          >
            <Stack spacing={1.6} component="form" onSubmit={handleChangePassword}>
              <Alert severity="info" sx={{ fontSize: 12 }}>
                Usa una contrasena robusta, evita repetir claves de otros sistemas y manten tu
                acceso bajo control personal.
              </Alert>

              {error ? (
                <Alert severity="error" sx={{ fontSize: 12 }}>
                  {error}
                </Alert>
              ) : null}

              <TextField
                label="Contrasena actual"
                size="small"
                type="password"
                value={form.current_password}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, current_password: event.target.value }))
                }
                fullWidth
              />

              <Box>
                <TextField
                  label="Nueva contrasena"
                  size="small"
                  type="password"
                  value={form.new_password}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, new_password: event.target.value }))
                  }
                  fullWidth
                />
                <Box
                  sx={(currentTheme) => ({
                    width: "100%",
                    height: 4,
                    borderRadius: 999,
                    bgcolor: currentTheme.palette.grey[300],
                    overflow: "hidden",
                    mt: 0.9,
                  })}
                >
                  <Box
                    sx={{
                      width: `${passwordStrength}%`,
                      height: "100%",
                      background: getStrengthColor(passwordStrength),
                      transition: "width .2s, background .2s",
                    }}
                  />
                </Box>
              </Box>

              <Box>
                <TextField
                  label="Confirmar nueva contrasena"
                  size="small"
                  type="password"
                  value={form.new_password2}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, new_password2: event.target.value }))
                  }
                  fullWidth
                />
                <Typography
                  sx={{
                    display: "block",
                    mt: 0.8,
                    fontSize: 12,
                    color:
                      !form.new_password2
                        ? "text.secondary"
                        : passwordsMatch
                          ? "#1D9E75"
                          : "#E24B4A",
                  }}
                >
                  {!form.new_password2
                    ? "Confirma la nueva contrasena."
                    : passwordsMatch
                      ? "Las contrasenas coinciden"
                      : "Las contrasenas no coinciden"}
                </Typography>
              </Box>

              <Button
                type="submit"
                variant="contained"
                startIcon={<LockOutlined />}
                disabled={!canSubmit}
                sx={{ minHeight: 42, alignSelf: "flex-start" }}
              >
                {submitting ? "Guardando..." : "Actualizar contrasena"}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </Box>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}

export default ProfilePage;
